<template>
  <TableTemplate
    v-model="mappedWebsites"
    :columns="tableColumns"
    :columnVariables="tableColumnVariables"
    :capitalize="capitalize"
    :skeletonClass="skeletonClass"
    :selected="selected"
    :removeCursor="true"
    :enable-sorting="true"
    newly-created-alert-key="website"
    :page="page"
    :max="max"
    @update-column-order="updateColumnOrder"
    @set-page="(v) => (page = v)"
    @toggle-selected="toggleItem"
    ref="table"
  >
    <template #new-item-alert-content="{ domainname, uid }">
      U heeft een nieuw
      <router-link :to="`/admin/websites/edit/${uid}`">{{
        domainname
      }}</router-link>
      winkel.
    </template>

    <template #above>
      <TableHeader
        v-model="selecting"
        title="Winkel"
        :showButton="showButton"
        :refetch="refetch"
        :exportParams="exportEndpointParams"
        select-multiple-value="Winkel selecteren"
        @customize-layout="customizeLayout"
        @setting-layout="customizeSetting"
      >
        <template #header-buttons-before="props">
          <slot name="header-buttons-before" v-bind="props"></slot>
        </template>
        <template #after-title>
          <!-- <MultipleSelectOptions v-if="showButton" :options="editOptions(refetch)">
            <template #enable-selected>
              <svg xmlns="http://www.w3.org/2000/svg" class="button-icon" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </template>
            <template #disable-selected>
              <svg xmlns="http://www.w3.org/2000/svg" class="button-icon" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </template>
          </MultipleSelectOptions> -->

          <HeaderButton
            class="ml-2"
            buttonTypeClass="btn-outline-primary btn-primary-secondary-outline"
            style="padding: 11px 8px"
            @click.native="addWebsite"
          >
            Winkel toevoegen
          </HeaderButton>
        </template>
      </TableHeader>

      <div class="d-flex">
        <TableMainSearchField v-model="search" />
        <RefreshBtn :loading="isLoadingTableResult" @click.native="refresh" />
      </div>
    </template>

    <template #cell-status="{ value, item }">
      <button
        type="button"
        class="btn bolder"
        :class="
          value && value.toLowerCase() === 'up' ? 'btn-success' : 'btn-danger'
        "
        @click="
          toggleStatus(
            item.uid,
            value,
            value.toUpperCase() !== 'UP'
              ? 'Winkel geactiveerd!'
              : 'Winkel gedeactiveerd!'
          )
        "
      >
        {{ value && value.toUpperCase() === "UP" ? "online" : "offline" }}
      </button>
    </template>

    <template v-if="loaded" #row-action="{ item }">
      <div class="delete-icon" @click="confirmDelete(item)">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </div>
    </template>

    <template v-if="loaded" #row-action="{ item }">
      <TableActionDropdown
        class="drop"
        :item="item"
        :options="options(item, freshRefetch)"
      />
    </template>

    <template #cell-active="{ value, item }">
      <FormButtonCheckbox
        :active="item.active"
        @click.native.stop.prevent="activateWebsite(item, value)"
      >
        {{ item.active ? "Actief" : "Inactief" }}
      </FormButtonCheckbox>
    </template>

    <template #cell-shipment_price="{ value, item }">
      <EditablePrice
        :value="value"
        @save="(price) => updateWebsiteShipmentPrice(item.uid, price)"
      />
    </template>

    <template #cell-name="nameProps">
      <slot name="name" v-bind="nameProps" :refetch="refetch">
        <router-link
          class="name-wrapper website-edit-link"
          @click.native="setLocalStorageTopDistance($event)"
          :to="`/admin/websites/edit/${nameProps.item.uid}`"
        >
          {{ nameProps.value || "---" }}
        </router-link>
      </slot>
    </template>

    <template #empty-state>
      <EmptyStatePlaceholder title="Er zijn geen winkel">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
            clip-rule="evenodd"
          />
        </svg>
      </EmptyStatePlaceholder>
    </template>
  </TableTemplate>
</template>

<script>
import TableTemplate from "src/layout/Table/TableTemplate";
import tableFieldOptions from "src/mixins/tableFieldOptions";
import TableSearchField from "src/components/Inputs/TableSearchField.vue";
import FormSelect from "src/components/Inputs/FormSelect.vue";
import TableActionDropdown from "src/components/Inputs/TableActionDropdown.vue";
import EmptyStatePlaceholder from "src/layout/EmptyStatePlaceholder.vue";
import TableCell from "src/components/Table/TableCell.vue";
import TableHeader from "src/components/Product/ProductTable/TableHeader.vue";
import FormButtonCheckbox from "src/components/Inputs/FormButtonCheckbox.vue";
import EditablePrice from "../layout/EditTableItem/EditablePrice.vue";
import MultipleSelectOptions from "../layout/Table/MultipleSelectOptions.vue";
import TableMainSearchField from "../layout/TableMainSearchField.vue";
import RefreshBtn from "../layout/RefreshBtn.vue";

import HeaderButton from "src/components/Inputs/HeaderButton.vue";

import keepScroll from "src/mixins/keepScroll";
import table from "src/mixins/table";
import selectMultipleItems from "src/mixins/selectMultipleItems";
import { websiteTypesOptions } from "../mixins/imageFileTypes";

export default {
  name: "WebsitesPage",
  metaInfo: {
    title: "Websites",
  },
  data() {
    return {
      /**
       * [ Mixin keepScroll ] - localStorageKey: Unique key(one per page) that will be used as a reference to set/get the element's distance from the top
       */
      localStorageKey: "position_scroll_websites",
    };
  },
  components: {
    RefreshBtn,
    MultipleSelectOptions,
    TableCell,
    TableHeader,
    TableMainSearchField,
    EditablePrice,
    TableSearchField,
    FormSelect,
    EmptyStatePlaceholder,
    TableTemplate,
    FormButtonCheckbox,
    HeaderButton,
    TableActionDropdown,
  },
  mixins: [
    selectMultipleItems,
    tableFieldOptions,
    keepScroll,
    table({
      pageReference: "websites",

      page_size: 30,
      defaultColumnSizes: {
        status: 110,
        size: 110,
        products: 110,

        primary_color: 140,
        secondary_color: 140,
        accent_color: 140,
        language: 110,
        vat_low: 110,
        active: 110,
      },
      minimumColumnSizes: {
        google_analytics_account_number: 230,
        google_adwords_conversion_id: 230,
        customer_support_email: 260,
        minimum_order_amount_free_shipping: 320,
        minimum_order_amount: 320,
      },
      columnTranslationMaps: {
        Naam: "name",
        Domeinnaam: "domainname",
        "E-mail voor klantenondersteuning": "customer_support_email",
        Grootte: "size",
        Taal: "language",
        "Primaire kleur": "primary_color",
        "Secundaire kleur": "secondary_color",
        "Accent kleur": "accent_color",
        Adres: "address",
        "Btw laag": "vat_low",
        "Btw hoog": "vat_high",
        Actief: "active",
        "Seo-metatitel": "seo_meta_title",
        "Seo-metabeschrijving": "seo_meta_description",
        "Google analytics rekeningnummer": "google_analytics_account_number",
        "Google adwords conversie-ID": "google_adwords_conversion_id",
        "Minimum bestelbedrag gratis verzending": "minimum_order_amount_free_shipping",
        "Minimaal bestelbedrag": "minimum_order_amount",
        "Verzending prijs:": "shipment_price",
        Producten: "products",
        /*Toestand: "status",*/
        "Soort winkel": "store_type",
      },
      imageTypeOptions: websiteTypesOptions,
      getImageEndpoint: (item) => `/api/websites/${item.uid}/files`,

      defaultColumns: ["Naam", "Grootte", "Producten", "Actief"],
      columnConfigurationId: "websites",

      params: {
        search: "",
        status: null,
        size: null,
        product: "",
        name: "",
      },
      route: "/api/websites",
    }),
  ],
  mounted() {
    this.freshRefetch();
  },
  methods: {
    addWebsite() {
      this.$router.push("/admin/websites/new").catch(() => {});
    },
    updateWebsite(website) {
      this.$router.push(`/admin/websites/edit/${website.uid}`).catch(() => {});
    },
    async deleteWebsite(uid) {
      await this.$handlePromise(this.axios.delete(`/api/websites/${uid}`));
      this.$store.dispatch('clearCacheSearch', "websites")
      this.$success("Website verwijderd!");
      this.freshRefetch();
    },
    options(item, freshRefetch) {
      return [
        {
          click: () => this.updateWebsite(item),
          id: "update-table-element",
          name: "Winkel bijwerken",
          icon: "nc-icon nc-ruler-pencil",
        },
        {
          click: () => this.confirmDelete(item, item.uid, freshRefetch),
          id: "delete-table-element",
          name: "Winkel verwijderen",
          icon: "nc-icon nc-simple-remove",
        },
      ];
    },

    editOptions(refetch) {
      return [
        {
          name: "Geselecteerde inschakelen",
          id: "enable-selected",
          action: () => this.enableSelected(refetch),
        },
        {
          name: "Geselecteerd uitschakelen",
          id: "disable-selected",
          action: () => this.disableSelected(refetch),
        },
      ];
    },
    confirmDelete(item) {
      this.$confirmAction(() => this.deleteWebsite(item.uid), {
        title: "Winkel verwijderen",
        description: `Are you sure you want to delete the <b>${item.domainname}</b> website?`,
        buttonSave: "Verwijderen",
      });
    },

    async toggleStatus(uid, value, message = "Verzendingsprijs geüpdatet!") {
      await this.updateSingleTableRowById(uid, { status: value.toUpperCase() === "UP" ? "DOWN" : "UP" }, message)
    },

    async activateWebsite(web) {
      this.$pushModal({
        modal: "ConfirmAction",
        toBind: {
          title: !web.active ? "Website activeren" : "Website deactiveren",
          description: !web.active ? `Weet u zeker dat u de site <b>${web.name}</b> wilt activeren?` : `Weet u zeker dat u de site <b>${web.name}</b> wilt deactiveren?`,
          buttonSave: !web.active ? "Activeren" : "Deactiveren",
          callback: () => {
            this.updateSingleTableRowById(web.uid, { active: !web.active }, !web.active
              ? "Winkel succesvol geactiveerd!"
              : "Winkel succesvol gedeactiveerd!")
          },
        },
      })
    },

    async updateWebsiteShipmentPrice(uid, price) {
      await this.updateSingleTableRowById(uid, { shipment_price: price }, "Verzendingsprijs geüpdatet!")
    },

    async updateWebsiteAction(uid, payload) {
      const res = await this.$handlePromise(
        this.axios.put(`/api/websites/${uid}`, payload)
      );
      this.$store.dispatch('clearCacheSearch', "websites")
      return res
    },

    async toggleWebsiteStatus(web, status) {
      await this.updateWebsiteAction(web.uid, {
        status: status.toUpperCase() === "UP" ? "DOWN" : "UP",
      });

      this.$success("Winkel geüpdatet!");
      this.freshRefetch();
    },
    async enableSelected() {
      console.log(3, this.selected);
      if (
        await Promise.all(
          this.selected.map((uid) =>
            this.updateWebsiteAction(uid, { active: true })
          )
        )
      ) {
        this.$success("Succesvol ingeschakelde Winkel!");
        this.refetch();
      }
    },
    async disableSelected() {
      if (
        await Promise.all(
          this.selected.map((uid) =>
            this.updateWebsiteAction(uid, { active: false })
          )
        )
      ) {
        this.$success("Winkel succesvol uitgeschakeld!");
        this.refetch();
      }
    },
  },
  computed: {
    mappedWebsites() {
      return this.tableResult.map
        ? this.tableResult.map((website) => ({
            ...website,
            products: `${website.products || 0} producten`,
          }))
        : this.tableResult;
    },
    table() {
      return this.$refs.table;
    },
    capitalize() {
      return [false, true, true, true];
    },
  },
};
</script>

<style scoped>
.name-wrapper {
  color: currentColor;
}

.name-wrapper:hover {
  text-decoration: underline;
}
</style>
